/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    Directive,
    ElementRef,
    AfterContentInit,
    OnDestroy,
    Input,
    AfterViewInit,
    NgZone,
    DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccessibilityService } from 'crimson/lib/accessibility/accessibility.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { FocusManager, FocusSearchPolicy } from '../services/focus-manager';
import {
    NavigationEvent,
    SpatialNavigationDirection
} from '../services/spatial-navigation/spatial-navigation-polyfill';
import { SpatialNavigationService } from '../services/spatial-navigation/spatial-navigation.service';
import { ContainFocusArgs } from '../services/spatial-navigation/spatial-navigation.types';

/**
 * Directives that provide helpers for spatial navigation containers.
 *
 * A spatial navigation container helps inform the navigation algorithm about layout in ways that aren't
 * inferrable from the DOM layout.
 *
 * See https://drafts.csswg.org/css-nav-1/#container for details
 */

@Directive({
    selector: '[nvSpatialNavigationContainer]',
    standalone: true
})
export class SpatialNavigationContainerDirective implements AfterContentInit, AfterViewInit, OnDestroy {
    // 'grid' basically navigates more strictly along the desired direction than 'normal' does.
    @Input() navigateFunction: 'grid' | 'normal' = 'grid';

    /**
     * Controls `spatial-navigation-action`.
     * Check https://drafts.csswg.org/css-nav-1/#css-property-spatialnavigationaction for more details.
     * For how to choose the suitable action, please check this guide
     * ui/libraries/hig/src/lib/services/spatial-and-back-navigation.md#how-to-choose-navigateAction-among-auto-focus-and-scroll
     */
    @Input() navigateAction: 'auto' | 'focus' | 'scroll' = 'auto';

    /**
     * Keeps focus within the navigation container
     * if a boolean value is given, prevent a user from navigating outside the container on all directions.
     * if a array of directions is given, only prevent a user from navigating outside the container for given
     * directions.
     */
    @Input() get containFocus() {
        return this._containFocus;
    }
    set containFocus(value: boolean | SpatialNavigationDirection[]) {
        if (Array.isArray(value)) {
            this._containFocus = value.slice();
        } else {
            this._containFocus = coerceBooleanProperty(value);
        }
    }
    private _containFocus: ContainFocusArgs = true;

    /** Makes the container focusable, but transit focus to its child when being focused */
    @Input() get captureFocusForChild() {
        return this._captureFocusForChild;
    }
    set captureFocusForChild(value: any) {
        this._captureFocusForChild = coerceBooleanProperty(value);
        this.updateTabIndex();
    }
    private _captureFocusForChild = false;

    /**
     * Skip updating the tabIndex on the container, helpful for cases where adding a tabindex (even 0 or -1) might
     * cause screen readers to treat the container as focusable
     */
    @Input() get skipTabIndexUpdate() {
        return this._skipTabIndexUpdate;
    }
    set skipTabIndexUpdate(value: any) {
        this._skipTabIndexUpdate = coerceBooleanProperty(value);
    }
    private _skipTabIndexUpdate = false;

    /**
     * Decide how to move focus into child elements after the container capture the focus
     *
     * `first`: select the first focusable element in the container.
     *
     * `initial`: select the element with cdkFocusInitial first, fallback to the first focusable if cdkFocusInitial not found.
     *
     * `closest`: select the element that has the closest distance from current focused element in the given direction.
     *
     */
    @Input() captureAndTransitFocusPolicy: FocusSearchPolicy | 'closest' = 'initial';

    /**
     * automatically stop capturing the focus for child nodes if there are no focusable child nodes in the container.
     * note that enabling this might cause a performance impact since the directive traverses the container's child tree
     * when initialized or captureFocusForChild is disabled then enabled later via template binding.
     */
    @Input() get autoDisableCaptureIfNoChild() {
        return this._autoDisableCaptureIfNoChild;
    }
    set autoDisableCaptureIfNoChild(value: any) {
        this._autoDisableCaptureIfNoChild = coerceBooleanProperty(value);
    }
    private _autoDisableCaptureIfNoChild = false;

    // FIXME: shoule be removed after AVENGERS-2313 is done.
    @Input() get enableSmoothScrolling() {
        return this._enableSmoothScrolling;
    }
    set enableSmoothScrolling(value: any) {
        this._enableSmoothScrolling = coerceBooleanProperty(value);
    }
    private _enableSmoothScrolling = true;

    @Input() set hideFromScreenReader(value: any) {
        const booleanValue = coerceBooleanProperty(value);
        if (this._hideFromScreenReader !== booleanValue) {
            this._hideFromScreenReader = booleanValue;
            this.updateTabIndex();
        }
    }
    get hideFromScreenReader(): boolean {
        return this._hideFromScreenReader;
    }
    private _hideFromScreenReader = false;

    private screenReaderNavigating = false;
    private logger: Logger;

    constructor(
        private element: ElementRef<HTMLElement>,
        private spatialNavigationService: SpatialNavigationService,
        private focusManager: FocusManager,
        private ngZone: NgZone,
        private accessibilityService: AccessibilityService,
        private destroyRef: DestroyRef,
        logging: LoggingService
    ) {
        this.logger = logging.getLogger('SpatialNavigationContainer');
    }

    ngAfterContentInit() {
        if (!this.spatialNavigationService.enabled) {
            return;
        }

        this.spatialNavigationService.setNavigationContainerCssStyles(
            this.element.nativeElement,
            this.navigateFunction,
            this.navigateAction,
            this.enableSmoothScrolling
        );
    }

    ngAfterViewInit() {
        if (!this.spatialNavigationService.enabled) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this.element.nativeElement.addEventListener('navbeforefocus', this.navBeforeFocus);
            this.element.nativeElement.addEventListener('navnotarget', this.navNoTarget);
        });

        this.accessibilityService
            .onScreenReaderNavigating()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(screenReaderNavigating => {
                this.screenReaderNavigating = screenReaderNavigating;
                this.ngZone.run(() => {
                    this.updateTabIndex();
                });
            });

        this.updateTabIndex();
    }

    ngOnDestroy() {
        if (!this.spatialNavigationService.enabled) {
            return;
        }

        this.element.nativeElement.removeEventListener('navbeforefocus', this.navBeforeFocus);
        this.element.nativeElement.removeEventListener('navnotarget', this.navNoTarget);
    }

    private navBeforeFocus = (event: NavigationEvent) => {
        if (this.captureFocusForChild) {
            let needTransitFocus = event.target === this.element.nativeElement;
            if (
                this.autoDisableCaptureIfNoChild &&
                !this.spatialNavigationService.isFocusable(this.element.nativeElement)
            ) {
                this.updateTabIndex();
                // no need to transit focus if the focus is already inside the container
                needTransitFocus = needTransitFocus || !this.element.nativeElement.contains(document.activeElement);
            }

            if (!needTransitFocus) {
                return;
            }

            let target: HTMLElement = null;
            if (this.captureAndTransitFocusPolicy === 'closest') {
                // If the active element is an ancestor of the current spatial navigation container,
                // (a better way to say this is that the current spatial navigation container is an internal candidate of the active element)
                // we use the container as the search origin to avoid infinite loop.
                // because it is certain that the search will find the container as the best candidate again if
                // we choose the active element as the search origin.
                const source = document.activeElement.contains(this.element.nativeElement)
                    ? this.element.nativeElement
                    : document.activeElement;

                target = this.spatialNavigationService.getClosestChild(
                    source as HTMLElement,
                    this.element.nativeElement,
                    event.detail.dir
                );
            } else {
                const isFocusable = this.spatialNavigationService.isFocusable.bind(this.spatialNavigationService);
                target =
                    this.captureAndTransitFocusPolicy === 'first'
                        ? this.focusManager.getFirstFocusableChild(this.element.nativeElement, isFocusable)
                        : this.focusManager.getInitialFocusable(this.element.nativeElement, isFocusable);
            }

            if (!target || target === this.element.nativeElement) {
                let reason = '';
                if (!target) {
                    reason = 'target not found';
                } else if (target === this.element.nativeElement) {
                    reason = `focus is on the container, container=${this.element.nativeElement.tagName}, containerClass=${this.element.nativeElement.className}`;
                }
                this.logger.info(`possible focus lost. reason=${reason}`);
            }

            event.preventDefault();
            event.stopPropagation();

            // dispatch the navbeforefocus again to inform parent element that the navigation target is changed
            if (target && target !== this.element.nativeElement) {
                this.focusManager.navigateTo(target, event.detail.dir);
            } else {
                // if we cannot find a target inside the container, it indicates that the container doesn't have
                // any focusable child. we should update its tabindex here to prevent it from receiving focus again.
                if (this.autoDisableCaptureIfNoChild) {
                    this.updateTabIndex();
                    // manually trigger a spatial navigation again as we should allow focus pass through this container
                    // if autoDisableCaptureIfNoChild is set to true
                    this.spatialNavigationService.navigate(event.detail.dir);
                }
            }
        }
    };

    // Prevents navigation outside of the current container, override with the containFocus input param
    private navNoTarget = (event: NavigationEvent) => {
        if (
            this.spatialNavigationService.shouldContainFocus(event.detail.dir, this.containFocus) &&
            event.target === this.element.nativeElement
        ) {
            event.preventDefault();
        }
    };

    updateTabIndex() {
        const shouldHideFromScreenReader = this.hideFromScreenReader && this.screenReaderNavigating;
        const element = this.element.nativeElement;

        if (
            (!this.skipTabIndexUpdate || shouldHideFromScreenReader) &&
            this.spatialNavigationService.enabled &&
            this.element
        ) {
            if (shouldHideFromScreenReader) {
                element.removeAttribute('tabindex');
            } else {
                // Set appropriate tabindex value
                const enableCaptureFocus =
                    this.captureFocusForChild && this.autoDisableCaptureIfNoChild
                        ? !!this.focusManager.getInitialFocusable(
                              element,
                              this.spatialNavigationService.isFocusable.bind(this.spatialNavigationService)
                          )
                        : this.captureFocusForChild;

                element.tabIndex = enableCaptureFocus ? 0 : -1;
            }
        }
    }
}

/**
 * Simplified version of the above directive, that only marks the container.
 */
@Directive({
    selector: '[nvSpatialNavigationGroup]',
    standalone: true
})
export class SpatialNavigationGroupDirective implements AfterContentInit {
    @Input() navigateFunction: 'grid' | 'normal' = 'grid';
    @Input() navigateAction: 'auto' | 'focus' | 'scroll' = 'auto';

    // FIXME: shoule be removed after AVENGERS-2313 is done.
    @Input() get enableSmoothScrolling() {
        return this._enableSmoothScrolling;
    }
    set enableSmoothScrolling(value: any) {
        this._enableSmoothScrolling = coerceBooleanProperty(value);
    }
    private _enableSmoothScrolling = true;

    constructor(
        private element: ElementRef,
        private spatialNavigationService: SpatialNavigationService
    ) {}

    ngAfterContentInit() {
        if (!this.spatialNavigationService.enabled) {
            return;
        }

        this.spatialNavigationService.setNavigationContainerCssStyles(
            this.element.nativeElement,
            this.navigateFunction,
            this.navigateAction,
            this.enableSmoothScrolling
        );
    }
}
